import { CommonModule } from '@angular/common';
import { Component, effect, EventEmitter, Inject, Input, input, OnInit, Output } from '@angular/core';
import { FormsModule, ReactiveFormsModule, UntypedFormControl } from '@angular/forms';
import { CrmDependencies, CrmInjectionToken, ObjectType, TranslationModule } from '@galaxy/crm/static';
import { BulkImportStepperService } from '../bulk-import-stepper.service';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { GalaxyFormFieldModule } from '@vendasta/galaxy/form-field';
import { FieldMappingService, FieldSchemaGroup } from './field-mapping.service';
import { combineLatest, map, Observable, of, startWith, switchMap, withLatestFrom } from 'rxjs';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatButtonModule } from '@angular/material/button';
import { MatDialogRef } from '@angular/material/dialog';
import { take } from 'rxjs/operators';
import { toObservable } from '@angular/core/rxjs-interop';
import { MatInput } from '@angular/material/input';

const DoNotImportOption = '';
const CUSTOM_OBJECT_PREFIX = 'CustomObject::';
const CUSTOM_OBJECT_DELIMITER = '::';

@Component({
  selector: 'crm-field-mapping-row',
  templateUrl: 'field-mapping-row.component.html',
  providers: [
    {
      provide: MatDialogRef,
      useValue: {},
    },
  ],
  styleUrls: ['field-mapping-row.component.scss'],
  imports: [
    CommonModule,
    TranslationModule,
    FormsModule,
    ReactiveFormsModule,
    MatAutocompleteModule,
    GalaxyFormFieldModule,
    MatButtonModule,
    MatIconModule,
    MatTooltipModule,
    MatInput,
  ],
})
export class CrmFieldMappingRowComponent implements OnInit {
  @Input() contactFieldMappingGroups: FieldSchemaGroup[] = [];
  @Input() companyFieldMappingGroups: FieldSchemaGroup[] = [];
  @Input() customObjectFieldMappingGroups: FieldSchemaGroup[] = [];
  @Input() opportunityFieldMappingGroups: FieldSchemaGroup[] = [];
  @Input() header = '';
  importType = input<ObjectType>();
  private readonly importType$ = toObservable(this.importType);

  @Output() initialImportType = new EventEmitter<ObjectType>();

  DoNotImportOption = DoNotImportOption;
  previousSelection = DoNotImportOption;
  searchFieldControl = new UntypedFormControl(DoNotImportOption);

  fieldSchemaGroups: FieldSchemaGroup[] = [];
  filteredSchemaGroups$!: Observable<FieldSchemaGroup[]>;
  initialMappingsSet = false;

  constructor(
    @Inject(CrmInjectionToken) private readonly config: CrmDependencies,
    private readonly stepperService: BulkImportStepperService,
    private readonly mappingService: FieldMappingService,
  ) {
    effect(() => {
      const importType = this.importType();
      if (importType) {
        this.setFieldSchemaGroups(importType as ObjectType);
        if (!this.initialMappingsSet) {
          this.initialMappingsSet = true;
        } else {
          this.removeMapping();
        }
      }
    });
  }

  ngOnInit() {
    const searchControl$: Observable<string> = this.searchFieldControl.valueChanges.pipe(startWith(DoNotImportOption));

    this.filteredSchemaGroups$ = combineLatest([searchControl$, this.importType$]).pipe(
      switchMap(([_, importType]) => {
        if (!importType) {
          return of([]);
        }
        return this.mappingService.availableFieldGroups$(importType);
      }),
      withLatestFrom(searchControl$),
      map(([groups, searchValue]) => {
        if (searchValue === DoNotImportOption) {
          return groups;
        }
        const filterValue = searchValue.toLowerCase();
        return groups.map((group) => ({
          ...group,
          schemaOptions: group.schemaOptions.filter(({ fieldName }) => fieldName.toLowerCase().includes(filterValue)),
        }));
      }),
    );

    this.stepperService.csvMappings$
      .pipe(
        map((mappings) => {
          return mappings.filter(({ headerName }) => headerName === this.header);
        }),
        take(1),
      )
      .subscribe((result) => {
        if (result.length > 0) {
          const mapping = result[0];
          const objectType = mapping?.crmObjectType || '';

          let fullObjectType: ObjectType;
          if (objectType === 'CustomObject' && mapping.crmObjectSubtype) {
            fullObjectType = `${CUSTOM_OBJECT_PREFIX}${mapping.crmObjectSubtype}` as ObjectType;
          } else {
            fullObjectType = objectType as ObjectType;
          }

          this.setInitialImportType(fullObjectType);
          const fieldId = mapping?.fieldId || '';
          this.setControlWithFieldName(fieldId, fullObjectType);
        }
      });
  }

  private setControlWithFieldName(fieldId: string, objectType?: ObjectType) {
    if (!fieldId) return;

    const crmObjectType = objectType || this.importType();
    if (!crmObjectType) return;

    let actualObjectType: ObjectType;
    let objectSubtype: string | undefined;

    if (typeof crmObjectType === 'string' && crmObjectType.startsWith(CUSTOM_OBJECT_PREFIX)) {
      actualObjectType = 'CustomObject' as ObjectType;
      objectSubtype = crmObjectType.split(CUSTOM_OBJECT_DELIMITER)[1];
    } else {
      actualObjectType = crmObjectType;
    }

    this.mappingService
      .listFieldGroups$(actualObjectType, objectSubtype)
      .pipe(take(1))
      .subscribe((groups) => {
        const allSchemas = [];
        for (const { schemaOptions } of groups) {
          allSchemas.push(...schemaOptions);
        }
        const foundField = allSchemas.find(({ fieldId: id }) => id === fieldId);
        if (foundField) {
          this.previousSelection = foundField.fieldName;
          this.searchFieldControl.setValue(this.previousSelection);
        }
      });
  }

  onSelectionChange(selectedId: string): void {
    const importType = this.importType();
    if (!selectedId || !importType) return;
    this.mappingService.addMapping(this.header, selectedId, importType);
    this.setControlWithFieldName(selectedId, importType);
  }

  revertInvalidSelection(): void {
    this.searchFieldControl.setValue(this.previousSelection);
  }

  removeMapping(): void {
    this.mappingService.removeMapping(this.header);
    this.searchFieldControl.setValue(DoNotImportOption);
    this.previousSelection = DoNotImportOption;
  }

  setInitialImportType(objectType: ObjectType): void {
    this.setFieldSchemaGroups(objectType);
    this.initialImportType.emit(objectType);
  }

  setFieldSchemaGroups(objectType: ObjectType): void {
    if (typeof objectType === 'string' && objectType.startsWith(CUSTOM_OBJECT_PREFIX)) {
      this.fieldSchemaGroups = this.customObjectFieldMappingGroups;
    } else {
      switch (objectType) {
        case 'Contact':
          this.fieldSchemaGroups = this.contactFieldMappingGroups;
          break;
        case 'Company':
          this.fieldSchemaGroups = this.companyFieldMappingGroups;
          break;
        case 'Opportunity':
          this.fieldSchemaGroups = this.opportunityFieldMappingGroups;
          break;
      }
    }
  }
}
